import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import {
  isAuthenticated,
  startLogin,
  startSilentLogin,
  discoverLoginMethod,
  rememberLoginHint,
  peekLoginHint,
  refreshSessionForNewMembership,
  getIdTokenClaims,
  logout,
} from '../lib/auth.js';
import {
  CLAIM_EXISTING_PATH, savePendingClaim, readPendingClaim, clearPendingClaim, rememberClaimAdded, takeClaimAdded,
} from '../lib/pending-claim.js';
import { loginPath } from '../lib/login-path.js';
import { config } from '../lib/config.js';
import { detectActiveStep } from '../lib/customer-status.js';
import { deploymentAccountId } from '../../../shared/customer-account.mjs';
import { markConsentStarted, hasStartedConsent, saveSessionLink, loadSessionLink, forgetSessionLink } from '../lib/consent-progress.js';
import { consentReturnsHere, CONSENT_LINK_MISMATCH } from '../lib/consent-url.js';
import { HeaderAccent, TopBarBrand } from '../components/TopBar.jsx';
import BrandFooter from '../components/BrandFooter.jsx';
import SectionCard from '../components/SectionCard.jsx';
import PageState from '../components/PageState.jsx';
import { Dot } from '../components/StatusWord.jsx';
import { permissionsForSelection } from '../../../shared/permission-map.mjs';
import { needsDataSourceChoice } from '../../../shared/start-small.mjs';
import PermissionConsentTable from '../components/PermissionConsentTable.jsx';
import DataSourceChoice from './onboarding/DataSourceChoice.jsx';
import InstallStep, { DoneStep } from './onboarding/InstallStep.jsx';
import {
  AWS_CONSOLE_SIGN_IN_URL,
  IAM_ACKNOWLEDGMENT,
  buildTrustStackLaunchUrl,
  collectorClientName,
  trustStackInstructionsText,
} from '../../../shared/trust-stack.mjs';

/**
 * Onboarding wizard: 4 steps with IPC Global branding.
 *
 *   1. Connect Entra ID (choose the data sources, then admin consent)
 *   2. Set up AWS access (Launch Stack)
 *   3. Install (starts on its own once AWS access is confirmed; progress from deploy-status)
 *   4. Done
 *
 * ENTRAID-350 (Hugo Andrade, 2026-10-26): no step whose only action is to continue. The two human moments stay explicit
 * because they happen outside the portal (Microsoft admin consent, creating the AWS stack); everything else moves on
 * by itself. An AWS account that differs from the AWS Marketplace account on record (or with none on record) keeps
 * its explicit confirmation and the IPC Global approval (SEC-004).
 */
const STEPS = [
  { id: 'consent', label: 'Connect Entra ID' },
  { id: 'trust', label: 'Set up AWS access' },
  { id: 'deploy', label: 'Install' },
  { id: 'done', label: 'Done' },
];

/**
 * ENTRAID-350: true when the AWS account of the registered trust role must be confirmed by the customer admin (and
 * approved by IPC Global) before the install starts: it differs from the AWS Marketplace account on record, or there
 * is none. A match advances on its own. The backend enforces the same rule at deploy (lib/trust-account.mjs).
 */
export function awsAccountNeedsConfirmation(customer, accountId) {
  const expected = ACCOUNT_ID_RE.test(String(customer?.awsAccountId ?? '')) ? customer.awsAccountId : null;
  return !expected || !accountId || expected !== accountId;
}

// The step to open for a customer record: a recorded trust role in an account that needs confirming opens Step 2
// (where the confirmation and the approval live), never an install that the backend would refuse.
function entryStep(customer) {
  const step = detectActiveStep(customer);
  if (step === 'deploy' && customer?.status === 'consent-confirmed'
    && awsAccountNeedsConfirmation(customer, deploymentAccountId(customer))) {
    return 'trust';
  }
  return step;
}

const CLAIM_STEP = { id: 'claim', label: 'Create your login' };

// Every sign-in from onboarding is email-first. With a known email (the one
// the claim form just used) we ask discovery which method serves its domain
// and go straight there: native -> the password form with the email prefilled,
// sso -> the tenant IdP. A customer therefore never lands on the hosted UI's
// default screen with the workforce IdP button. Without a known email we send
// the user to the email-first /login page, never straight to the hosted UI.
// The signed-in session's own email, lowercased; '' without a session.
function sessionEmail() {
  const email = getIdTokenClaims()?.email;
  return typeof email === 'string' ? email.trim().toLowerCase() : '';
}

async function signInFromOnboarding(returnTo, email, navigate) {
  const value = typeof email === 'string' ? email.trim() : '';
  if (!value) {
    navigate(loginPath(returnTo), { replace: true });
    return;
  }
  try {
    const { method, idpIdentifier } = await discoverLoginMethod(value);
    await startLogin(returnTo, { method, idpIdentifier, email: value });
  } catch {
    // startLogin navigates away on success; if it threw, fall back to /login.
    navigate(loginPath(returnTo), { replace: true });
  }
}

export default function Onboarding() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const customerId = params.get('customerId');
  // ENTRAID-350: back from sign-in to claim the link with an existing login (lib/pending-claim.js).
  const claimExisting = !token && params.get('claim') === 'existing';
  const [customer, setCustomer] = useState(null);
  const [signedIn, setSignedIn] = useState(false);
  // One-line notice, once, when this tab just added the customer to the login.
  const [addedToLogin, setAddedToLogin] = useState(false);
  const [activeStep, setActiveStep] = useState('consent');
  const [error, setError] = useState(null);
  const [claimPreview, setClaimPreview] = useState(null);

  useEffect(() => {
    // Marketplace onboarding-claim flow: a buyer following the onboarding
    // link has no login yet, so this branch never calls startLogin/getCustomer.
    if (token) {
      api.previewOnboarding(token)
        .then((r) => {
          if (r.state === 'claimed') {
            // Nothing left to ask: go straight to sign-in, no click-through.
            // The email is known only if this tab just made the claim.
            signInFromOnboarding(`/onboarding?customerId=${r.customerId}`, peekLoginHint(), navigate);
            return;
          }
          setClaimPreview(r);
        })
        .catch((e) => setError(e.message));
      return;
    }

    if (claimExisting) {
      if (isAuthenticated()) {
        setSignedIn(true);
        return;
      }
      if (startSilentLogin(CLAIM_EXISTING_PATH)) return;
      signInFromOnboarding(CLAIM_EXISTING_PATH, peekLoginHint(), navigate);
      return;
    }

    if (!isAuthenticated()) {
      // A reload drops the memory-only tokens (SEC-009): try a silent sign-in
      // first. When that was just tried (no session), go email-first: route by
      // the known email's domain, or through the email-first login page when it
      // is unknown; never a provider chooser.
      if (startSilentLogin(`/onboarding?customerId=${customerId}`)) return;
      signInFromOnboarding(`/onboarding?customerId=${customerId}`, peekLoginHint(), navigate);
      return;
    }
    api.getCustomer(customerId)
      .then((r) => {
        setAddedToLogin(takeClaimAdded(customerId));
        setCustomer(r.customer);
        // Step detection is in src/lib/customer-status.js so it's testable.
        // The "consent-confirmed" gate prevents customers with a pre-filled
        // entraTenantId (set by the operator) from skipping Step 1.
        setActiveStep(entryStep(r.customer));
      })
      .catch((e) => setError(e.message));
  }, [token, customerId, claimExisting]);

  if (claimExisting) {
    return (
      <OnboardingShell>
        <h1 className="text-2xl">Welcome, let's get onGuard running</h1>
        {signedIn ? <ClaimExistingStep /> : <Loading />}
      </OnboardingShell>
    );
  }

  if (token) {
    if (error) return <OnboardingShell><ErrorCard message={error} /></OnboardingShell>;
    // Covers both the initial fetch and the "claimed" state (which redirects
    // to login instead of setting claimPreview): either way, nothing to show
    // beyond the loading placeholder.
    if (!claimPreview) return <OnboardingShell><Loading /></OnboardingShell>;

    if (claimPreview.state === 'invalid' || claimPreview.state === 'expired') {
      return (
        <OnboardingShell>
          <ErrorCard message="This onboarding link is invalid or has expired. Ask your IPC Global contact for a new link." />
        </OnboardingShell>
      );
    }

    return (
      <OnboardingShell>
        <h1 className="text-2xl">
          Welcome, let's get onGuard running
        </h1>
        {claimPreview.companyName && (
          <p className="text-sm text-ipc-muted -mt-3">Setting up <strong>{claimPreview.companyName}</strong></p>
        )}
        <Steps steps={[CLAIM_STEP, ...STEPS]} active="claim" />
        <ClaimStep preview={claimPreview} token={token} />
      </OnboardingShell>
    );
  }

  if (error) return <OnboardingShell><ErrorCard message={error} /></OnboardingShell>;
  if (!customer) return <OnboardingShell><Loading /></OnboardingShell>;

  return (
    <OnboardingShell>
      <h1 className="text-2xl">
        Welcome, let's get onGuard running
      </h1>
      {customer.companyName && (
        <p className="text-sm text-ipc-muted -mt-3">Setting up <strong>{customer.companyName}</strong></p>
      )}
      {addedToLogin && (
        <p role="status" className="text-sm text-ipc-muted">
          {customer.companyName || 'This company'} was added to your onGuard login.
        </p>
      )}
      <Steps steps={STEPS} active={activeStep} />

      {activeStep === 'consent' && (
        <ConsentStep
          customer={customer}
          onNext={(c) => {
            // After a reconnect (ENTRAID-261) a deployed customer goes back
            // to its own step (done), never through Steps 2 and 3 again.
            const next = { ...c, entraConsentResetAt: undefined };
            setCustomer(next);
            const step = entryStep(next);
            setActiveStep(step === 'consent' ? 'trust' : step);
          }}
        />
      )}
      {activeStep === 'trust' && (
        <TrustStep
          customer={customer}
          onNext={(c) => { if (c) setCustomer(c); setActiveStep('deploy'); }}
        />
      )}
      {activeStep === 'deploy' && (
        <InstallStep
          customer={customer}
          onDone={(c) => { setCustomer(c); setActiveStep('done'); }}
          onReconnect={() => setActiveStep('consent')}
          onBackToTrust={() => setActiveStep('trust')}
        />
      )}
      {activeStep === 'done' && <DoneStep customer={customer} />}
    </OnboardingShell>
  );
}

/* ---------- Layout shell with IPC branding ---------- */

// The wizard keeps its full-screen layout, without the workbench sidebar
// (portal workbench spec), with the TopBar's logo and product link and the
// IPC Global BrandFooter.
function OnboardingShell({ children }) {
  return (
    <div className="flex min-h-screen flex-col bg-ipc-surface">
      <header className="on-dark bg-ipc-primary text-white shadow-md">
        <div className="mx-auto flex h-14 max-w-3xl items-center px-4">
          <TopBarBrand productTo="/" />
        </div>
        <HeaderAccent />
      </header>

      <main className="flex-1">
        <div className="mx-auto max-w-3xl space-y-6 px-4 py-8">
          {children}
        </div>
      </main>

      <BrandFooter />
    </div>
  );
}

/* ---------- Step indicator ---------- */

function Steps({ steps, active }) {
  const activeIdx = steps.findIndex((s) => s.id === active);
  return (
    <ol className="flex items-center gap-2 text-sm">
      {steps.map((s, i) => (
        <li key={s.id} className="flex items-center gap-2">
          <span className={`w-7 h-7 rounded-sm flex items-center justify-center text-xs font-semibold transition-colors
            ${i < activeIdx ? 'bg-emerald-500 text-white' :
              i === activeIdx ? 'bg-ipc-light text-white' :
              'bg-slate-200 text-slate-500'}`}>
            {i < activeIdx ? '✓' : i + 1}
          </span>
          <span className={`${i === activeIdx ? 'font-medium text-slate-800' : 'text-ipc-muted'} hidden sm:inline`}>{s.label}</span>
          {i < steps.length - 1 && <span className="text-slate-300 mx-1">-</span>}
        </li>
      ))}
    </ol>
  );
}

/* ---------- Step 1: Consent ---------- */

// How long "Link copied" stays on the Copy link button.
const COPY_FEEDBACK_MS = 3000;
// Backoff for the server-side consent check while the consent step is under
// way: about ten minutes in total, then only on focus.
export const CONSENT_POLL_DELAYS_MS = [3000, 5000, 8000, 13000, 20000, 30000];
const CONSENT_POLL_MAX_ATTEMPTS = 25;

/**
 * ENTRAID-350 "start small": a NEW customer that has not chosen its data sources yet chooses them first (Essentials by
 * default), so the Microsoft consent asks only for what the choice needs. A reconnect, an established (legacy, collecting
 * or lapsed) customer, or one with a recorded selection goes straight to the consent link and is never narrowed. The rule
 * is the backend's own (shared/start-small.mjs), so the two cannot diverge (security review of PR 395, M2).
 */
export { needsDataSourceChoice };

function ConsentStep({ customer, onNext }) {
  const [consentUrl, setConsentUrl] = useState(() => {
    const saved = loadSessionLink('consent', customer.customerId);
    return saved && consentReturnsHere(saved) ? saved : null;
  });
  // 'choose' (data sources first) or 'consent' (the Microsoft consent link).
  const [phase, setPhase] = useState(() => (consentUrl || !needsDataSourceChoice(customer) ? 'consent' : 'choose'));
  // The selection chosen in this session (null when it was recorded earlier).
  const [chosen, setChosen] = useState(null);
  const requested = chosen
    ? permissionsForSelection(chosen)
    : (Array.isArray(customer.permissionSelection?.permissions) ? customer.permissionSelection.permissions : null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Gate for the continue button: set once the user opened the consent page
  // or copied its link, and kept for this onboarding session (sessionStorage).
  const [started, setStarted] = useState(() => hasStartedConsent(customer.customerId));
  // 'idle' | 'copied' | 'failed'
  const [copyState, setCopyState] = useState('idle');
  const [announcement, setAnnouncement] = useState('');
  const copyTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(copyTimerRef.current), []);

  function startedExternalStep() {
    markConsentStarted(customer.customerId);
    setStarted(true);
  }

  async function copyLink() {
    startedExternalStep();
    clearTimeout(copyTimerRef.current);
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(consentUrl);
      setCopyState('copied');
      setAnnouncement('Consent link copied to the clipboard.');
      copyTimerRef.current = setTimeout(() => {
        setCopyState('idle');
        setAnnouncement('');
      }, COPY_FEEDBACK_MS);
    } catch {
      setCopyState('failed');
      setAnnouncement('Copying failed. The consent link is shown below so you can copy it manually.');
    }
  }

  // The consent link (SEC-001, ENTRAID-261): a Microsoft sign-in for the
  // Global Administrator that also grants the consent. The tenant is taken
  // from that sign-in, so there is nothing to type here. One link per
  // onboarding session (a reload reuses it); only a link whose redirect_uri
  // returns to this portal is offered (see lib/consent-url.js).
  const generate = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.startConsent(customer.customerId, {});
      if (!consentReturnsHere(r?.consentUrl)) {
        setConsentUrl(null);
        setErr(CONSENT_LINK_MISMATCH);
        return;
      }
      saveSessionLink('consent', customer.customerId, r.consentUrl, r.expiresInSeconds);
      setConsentUrl(r.consentUrl);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }, [customer.customerId]);

  useEffect(() => {
    if (phase === 'consent' && !consentUrl) generate();
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Server-side consent check (ENTRAID-260). Once the Global Administrator
  // has signed in through the link and Microsoft confirms the consent, Step 1
  // completes on its own, so an admin approving in another browser never has
  // to click anything here. Nothing from the browser is trusted for this.
  // verify.state: 'idle' | 'checking' | 'granted' | 'pending' | 'not-granted' | 'waiting-admin' | 'needs-approval' | 'error'
  const [verify, setVerify] = useState({ state: 'idle', attempts: 0, tenantVerified: false });
  const advancedRef = useRef(false);
  const inFlightRef = useRef(false);
  const canVerify = Boolean(consentUrl);

  const checkConsent = useCallback(async () => {
    if (advancedRef.current || inFlightRef.current) return;
    inFlightRef.current = true;
    setVerify((v) => ({ ...v, state: v.state === 'idle' ? 'checking' : v.state }));
    try {
      const r = await api.verifyConsent(customer.customerId);
      if (r?.verified) {
        advancedRef.current = true;
        setVerify((v) => ({ state: 'granted', attempts: v.attempts + 1, tenantVerified: true }));
        setAnnouncement('Microsoft confirmed the consent. Continuing to Step 2.');
        onNext({ ...customer, status: r.status === 'consent-pending' || !r.status ? 'consent-confirmed' : r.status });
        return;
      }
      const state = r?.state === 'tenant-not-verified' ? 'waiting-admin'
        : r?.state === 'needs-approval' ? 'needs-approval'
          : r?.state === 'not-granted' ? 'not-granted' : 'pending';
      setVerify((v) => ({ state, attempts: v.attempts + 1, tenantVerified: r?.tenantVerified === true }));
    } catch {
      setVerify((v) => ({ ...v, state: 'error', attempts: v.attempts + 1 }));
    } finally {
      inFlightRef.current = false;
    }
  }, [customer, onNext]);

  // Check as soon as the consent link exists, again with backoff while the
  // consent step is under way, and whenever this tab regains focus (the admin
  // may have approved in another tab or browser).
  useEffect(() => {
    if (!canVerify) return undefined;
    let timer;
    let stopped = false;
    let attempt = 0;
    const loop = async () => {
      await checkConsent();
      if (stopped || advancedRef.current) return;
      // Without a started consent step, one check is enough (focus re-checks).
      if (!hasStartedConsent(customer.customerId)) return;
      attempt += 1;
      if (attempt > CONSENT_POLL_MAX_ATTEMPTS) return;
      const delay = CONSENT_POLL_DELAYS_MS[Math.min(attempt - 1, CONSENT_POLL_DELAYS_MS.length - 1)];
      timer = setTimeout(loop, delay);
    };
    loop();
    const onFocus = () => { if (document.visibilityState !== 'hidden') checkConsent(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      stopped = true;
      clearTimeout(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [canVerify, checkConsent, customer.customerId, started]);

  // The manual fallback only once the admin's sign-in proved the tenant
  // (the backend refuses it otherwise): Microsoft's own check can lag.
  const verifyLagging = ['pending', 'not-granted'].includes(verify.state) && verify.tenantVerified;

  return (
    <SectionCard as="div" bodyClassName="space-y-5">
      <div>
        <h2>Step 1: Connect your Microsoft Entra ID</h2>
        <p className="text-sm text-ipc-muted mt-1">
          onGuard collects identity and security data from your Microsoft Entra ID tenant.
          To do this, a <strong>Global Administrator</strong> from your organization needs to
          sign in once with Microsoft and grant read-only access.
        </p>
        {phase === 'choose' ? (
          <p className="text-sm text-ipc-muted mt-1">
            First choose what onGuard reads. Microsoft then asks only for the permissions your choice needs.
          </p>
        ) : null}
      </div>

      {phase === 'choose' ? (
        <DataSourceChoice
          customer={customer}
          onChosen={(selection) => { setChosen(selection); setPhase('consent'); }}
        />
      ) : null}

      {err && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 space-y-2" role="alert">
          <p>{err}</p>
          <button type="button" className="btn-secondary text-sm" onClick={generate} disabled={busy}>
            {busy ? 'Preparing...' : 'Try again'}
          </button>
        </div>
      )}
      {phase === 'consent' && !consentUrl && !err && <p className="text-sm text-slate-500">Preparing the consent link...</p>}

      {consentUrl && (
        <div className="space-y-4">
          {/* A left-rule notice (the role name keeps Microsoft's capitals, so it is a strong line, not a heading). */}
          <div className="rounded-xl border border-ipc-border p-4 text-sm space-y-2" style={{ borderLeft: '4px solid var(--warn)' }}>
            <p className="font-semibold text-ipc-ink">Action required: Global Administrator consent</p>
            <p className="text-ipc-ink">
              A <strong>Global Administrator</strong> opens the link below, signs in with their
              work account, and then clicks <strong>"Accept"</strong> on the Microsoft permissions page
              that follows. onGuard learns your tenant from that sign-in. If you are the Global Admin, click the
              button directly. Otherwise, copy the link and send it to your admin; it works for 24 hours.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <a className="btn-primary flex-1 text-center" href={consentUrl} target="_blank" rel="noopener noreferrer"
               onClick={startedExternalStep} onAuxClick={startedExternalStep}>
              Open consent page (requires Global Admin)
            </a>
            <button type="button" className="btn-secondary inline-flex items-center justify-center gap-1.5" onClick={copyLink}>
              {copyState === 'copied' ? (
                <>
                  <svg className="w-4 h-4 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
                    <path d="m5 12 5 5 9-11" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Link copied
                </>
              ) : 'Copy link'}
            </button>
          </div>

          {/* Screen readers hear the copy outcome; sighted users see the label change. */}
          <p className="sr-only" aria-live="polite" role="status">{announcement}</p>

          {copyState === 'failed' && (
            <div className="space-y-1">
              <label htmlFor="consent-link-manual" className="label">
                Your browser blocked copying. Select the link below and copy it manually.
              </label>
              <input
                id="consent-link-manual"
                className="input font-mono text-xs"
                readOnly
                autoFocus
                value={consentUrl}
                onFocus={(e) => e.target.select()}
              />
            </div>
          )}

          <details className="text-xs text-slate-500" open={Boolean(chosen)}>
            <summary className="cursor-pointer hover:text-slate-700 font-medium">What does this consent grant?</summary>
            {requested ? (
              <div className="mt-2"><PermissionConsentTable permissions={requested} /></div>
            ) : (
              <p className="mt-2 text-slate-600">
                onGuard requests <strong>read-only</strong> access to: users, groups, directory roles,
                sign-in logs, audit logs, conditional access policies, secure score, and device
                information. It does <strong>not</strong> modify any data in your directory.
              </p>
            )}
          </details>

          <div className="border-t border-slate-200 pt-4 space-y-3">
            {/* Automatic confirmation: the normal path. */}
            <div className="flex items-start gap-2 text-sm" data-consent-verify={verify.state}>
              {verify.state === 'granted' ? (
                <p className="text-emerald-700">Microsoft confirmed the consent. Continuing to Step 2.</p>
              ) : (
                <>
                  <svg className="mt-0.5 h-4 w-4 flex-shrink-0 animate-spin text-ipc-light" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <p className="text-slate-600">
                    After the Global Admin signs in and clicks <strong>"Accept"</strong> on Microsoft's
                    consent page, this step completes on its own: onGuard checks with Microsoft that the
                    consent is in place.
                  </p>
                </>
              )}
            </div>

            {verify.state === 'needs-approval' && (
              <div className="rounded-md border border-ipc-border p-3" style={{ borderLeft: '4px solid var(--warn)' }} role="status">
                <p className="text-sm text-ipc-ink">
                  Microsoft confirmed the consent, but that Microsoft Entra tenant does not list your
                  email domain as verified. IPC Global approves the tenant before onboarding continues:
                  contact <a className="text-ipc-light hover:underline" href="mailto:support@ipc-global.com">support@ipc-global.com</a>.
                </p>
              </div>
            )}

            {verify.state === 'error' && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
                <p className="text-sm text-slate-700">We could not reach Microsoft to confirm the consent.</p>
                <button type="button" className="btn-secondary text-sm" onClick={checkConsent}>Check again</button>
              </div>
            )}

            {/* Manual fallback, only once the admin's sign-in proved the tenant. */}
            {verifyLagging && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
                <p className="text-sm text-slate-700">
                  Microsoft has not confirmed the consent yet. It can take a minute to show up.
                  {' '}If the Global Admin already clicked Accept, you can continue; the deployment in
                  Step 3 checks the access again.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <button type="button" className="btn-secondary text-sm" onClick={checkConsent}>
                    Check again
                  </button>
                  <button className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!started}
                    aria-describedby={started ? undefined : 'consent-continue-help'}
                    onClick={async () => {
                      try {
                        await api.startConsent(customer.customerId, { confirmConsent: true });
                        onNext({ ...customer, status: 'consent-confirmed' });
                      } catch (e) {
                        setErr(e.message);
                      }
                    }}>
                    Consent granted, continue to Step 2
                  </button>
                </div>
                {!started && (
                  <p id="consent-continue-help" className="text-xs text-slate-500">
                    Open the consent page or copy the link first. This button unlocks once the
                    admin-consent step has started.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </SectionCard>
  );
}

/* ---------- Step 0 (claim flow only): Create your login ---------- */

function ClaimStep({ preview, token }) {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState(preview.companyName || '');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);
  // The email the claim was made with: the sign-in below routes by it.
  const [claimedEmail, setClaimedEmail] = useState('');
  const [signingIn, setSigningIn] = useState(false);

  const isRetry = preview.state === 'retry-provisioning';

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr(null);
    const submittedEmail = email.trim().toLowerCase();
    try {
      const r = await api.claimOnboarding({
        token,
        email: submittedEmail,
        companyName: companyName.trim() || undefined,
      });
      setClaimedEmail(submittedEmail);
      // Survives a reload of this tab (sessionStorage), cleared once sign-in starts.
      rememberLoginHint(submittedEmail);
      setResult(r);
    } catch (e) {
      // ENTRAID-350: this address already has an onGuard login. Nothing to decide here: straight to sign-in with
      // the address as login hint, back to the signed-in claim. The token waits in this tab (lib/pending-claim.js).
      if (e.status === 409 && e.body?.error === 'existing_login'
          && savePendingClaim({ token, companyName: companyName.trim() || undefined, email: submittedEmail })) {
        // Already signed in AS this address: no trip to the IdP. Signed in as anyone else: sign in again with this
        // address as hint, never claim for the other session (security review of PR 416).
        if (isAuthenticated() && sessionEmail() === submittedEmail) navigate(CLAIM_EXISTING_PATH, { replace: true });
        else await signInFromOnboarding(CLAIM_EXISTING_PATH, submittedEmail, navigate);
        return;
      }
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (result) {
    return (
      <SectionCard as="div" title="Check your email" bodyClassName="space-y-4">
        <p className="text-sm text-ipc-muted">
          If this is your first IPC Global login, we sent a temporary password to{' '}
          <strong>{result.adminEmail}</strong>. If you already sign in to another IPC Global
          application with this email, use that password. Sign in to continue.
        </p>
        <button
          className="btn-primary disabled:opacity-60"
          disabled={signingIn}
          onClick={async () => {
            setSigningIn(true);
            await signInFromOnboarding(`/onboarding?customerId=${result.customerId}`, claimedEmail, navigate);
            setSigningIn(false);
          }}
        >
          {signingIn ? 'Taking you to sign-in…' : 'Sign in'}
        </button>
      </SectionCard>
    );
  }

  return (
    <SectionCard as="div" bodyClassName="space-y-5">
      <div>
        <h2>Step 1: Create your login</h2>
        <p className="text-sm text-ipc-muted mt-1">
          {isRetry
            ? 'Your login was not created last time. Enter the same email to try again.'
            : 'Confirm a couple of details and we will email you a temporary password to sign in.'}
        </p>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="claim-company-name" className="label">Company name</label>
          <input
            id="claim-company-name"
            className="input"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Acme Corp"
          />
        </div>
        <div>
          <label htmlFor="claim-email" className="label">Work email</label>
          <input
            id="claim-email"
            className="input"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
          {preview.invitedEmailMasked && !isRetry && (
            <p className="text-xs text-slate-500 mt-1">
              This invitation was sent to {preview.invitedEmailMasked}. Use that address.
            </p>
          )}
          {isRetry && preview.adminEmailMasked && (
            <p className="text-xs text-slate-500 mt-1">Previously used: {preview.adminEmailMasked}</p>
          )}
        </div>
        {err && <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
        <button className="btn-primary" disabled={busy || !email.trim()}>
          {busy ? 'Creating...' : 'Create login'}
        </button>
      </form>
    </SectionCard>
  );
}

/* ---------- Claim with an existing login (ENTRAID-350) ---------- */

// Signed in, back from the IdP: claim the pending link for this login (the server uses the sign-in's own email),
// then renew the session so the new customer is in the token, and land in its wizard.
function ClaimExistingStep() {
  const [err, setErr] = useState(null);
  const [canRetry, setCanRetry] = useState(false);
  const [otherAccount, setOtherAccount] = useState(false);
  const [busy, setBusy] = useState(true);
  const started = useRef(false);

  const run = useCallback(async () => {
    const pending = readPendingClaim();
    if (!pending) {
      setBusy(false);
      setCanRetry(false);
      setErr('This sign-in is not linked to an invitation any more. Open the invitation link again to continue.');
      return;
    }
    // The IdP may have kept another account's session: never claim for an address other than the one typed.
    if (pending.email && sessionEmail() !== pending.email) {
      clearPendingClaim();
      setBusy(false);
      setCanRetry(false);
      setOtherAccount(true);
      setErr(`You are signed in with a different account. Sign out, then sign in as ${pending.email} to add this company to that login.`);
      return;
    }
    setBusy(true); setErr(null);
    try {
      // Never an email: the server claims for the sign-in's own trusted address.
      const r = await api.claimExistingOnboarding({ token: pending.token, companyName: pending.companyName });
      clearPendingClaim();
      rememberClaimAdded(r.customerId);
      refreshSessionForNewMembership(`/onboarding?customerId=${encodeURIComponent(r.customerId)}`);
    } catch (e) {
      if (e.sessionRenewing) return;
      // A refusal is final (wrong address, used or expired link, refused address): the link is dropped from this tab.
      // A server or network failure keeps it, so trying again sends the same claim.
      const retryable = !e.status || e.status >= 500;
      if (!retryable) clearPendingClaim();
      setCanRetry(retryable);
      setErr(e.message);
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    run();
  }, [run]);

  if (!err) return <Loading />;
  return (
    <SectionCard as="div" bodyClassName="space-y-4">
      <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>
      {canRetry && (
        <button className="btn-primary disabled:opacity-60" disabled={busy} onClick={run}>
          {busy ? 'Trying again...' : 'Try again'}
        </button>
      )}
      {otherAccount && (
        <button className="btn-primary" onClick={() => logout()}>Sign out</button>
      )}
    </SectionCard>
  );
}

/* ---------- Step 2: Trust role ---------- */

// Backoff for trust stack detection: quick at first (the stack takes about a
// minute), then every 30 s, for about half an hour; focus re-checks after that.
export const TRUST_POLL_DELAYS_MS = [5000, 5000, 10000, 10000, 15000, 20000, 30000];
const TRUST_POLL_MAX_ATTEMPTS = 70;
export const TRUST_APPROVAL_POLL_MS = 30000;
const ACCOUNT_ID_RE = /^\d{12}$/;

function formatAccountId(id) {
  return String(id ?? '').replace(/^(\d{4})(\d{4})(\d{4})$/, '$1 $2 $3');
}

function TrustStep({ customer, onNext }) {
  const trustTemplateUrl = `${config.templatesBaseUrl}/customer-trust-stack.yaml`;

  // Step 2 launch link (SEC-002, ENTRAID-263): the backend mints a random,
  // single-use, 7-day nonce per link, so this page must not mint a new one on
  // every render or reload. One link per onboarding session (sessionStorage),
  // same pattern as the Step 1 consent link. If the mint call fails, fall
  // back to a link built from this page's config; that link carries no
  // registration parameters, so the customer enters the AWS account ID below.
  const savedTrustLink = loadSessionLink('trust', customer.customerId);
  const [launchStackUrl, setLaunchStackUrl] = useState(savedTrustLink);
  const [launchLoading, setLaunchLoading] = useState(!savedTrustLink);
  const [launchFallback, setLaunchFallback] = useState(false);
  const launchRequestedRef = useRef(false);

  const fetchLaunch = useCallback(() => {
    setLaunchLoading(true);
    api.getTrustLaunch(customer.customerId)
      .then((r) => {
        saveSessionLink('trust', customer.customerId, r.launchUrl, r.expiresInSeconds);
        setLaunchStackUrl(r.launchUrl);
        setLaunchFallback(false);
      })
      .catch(() => {
        setLaunchFallback(true);
        setLaunchStackUrl(buildTrustStackLaunchUrl({
          templatesBaseUrl: config.templatesBaseUrl,
          customerId: customer.customerId,
          // Customer isolation: the role is scoped to this collector name (the same rule the deploy uses).
          clientName: collectorClientName(customer),
          portalLambdaRoleArn: config.portalLambdaRoleArn,
        }));
      })
      .finally(() => setLaunchLoading(false));
  }, [customer.customerId]);

  useEffect(() => {
    if (launchStackUrl || launchRequestedRef.current) return;
    launchRequestedRef.current = true;
    fetchLaunch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [showTemplate, setShowTemplate] = useState(false);
  const [templateBody, setTemplateBody] = useState(null);
  const [templateError, setTemplateError] = useState(null);

  async function toggleTemplate() {
    if (showTemplate) { setShowTemplate(false); return; }
    setShowTemplate(true);
    if (templateBody || templateError) return;
    try {
      const res = await fetch(trustTemplateUrl, { cache: 'no-cache' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setTemplateBody(await res.text());
    } catch (e) {
      setTemplateError(e.message || 'Failed to load template');
    }
  }

  // ---- Automatic detection (ENTRAID-260) ----
  // The trust stack reports itself to onGuard through its registration custom
  // resource; this polls GET trust-status until the role is recorded. With an
  // AWS account ID entered below, the backend also tries to assume the role
  // there (the fallback when the stack could not report itself).
  const [detected, setDetected] = useState(null); // { trustRoleArn, accountId }
  // ENTRAID-272: the AWS Marketplace purchasing account is the expected
  // deployment account, so the account check below starts with it.
  const marketplaceAccountId = ACCOUNT_ID_RE.test(String(customer.awsAccountId ?? '')) ? customer.awsAccountId : null;
  const [accountId, setAccountId] = useState(marketplaceAccountId ?? '');
  const [probeAccount, setProbeAccount] = useState('');
  const [accountError, setAccountError] = useState(null);
  const [announcement, setAnnouncement] = useState('');
  const [pollEpoch, setPollEpoch] = useState(0);
  const [resetError, setResetError] = useState(null);
  const doneRef = useRef(false);

  // "Not my account" (review SEC-002): forget the detected role, void every
  // outstanding launch link, and start over with a fresh one.
  async function notMyAccount() {
    setResetError(null);
    try {
      await api.resetTrust(customer.customerId);
    } catch (e) {
      setResetError(e.message);
      return;
    }
    forgetSessionLink('trust', customer.customerId);
    setDetected(null);
    setLaunchStackUrl(null);
    doneRef.current = false;
    setAnnouncement('The detected AWS account was cleared. Launch the stack again in the right account.');
    fetchLaunch();
    setPollEpoch((n) => n + 1);
  }

  // Detection stops polling. ENTRAID-350: when the detected account IS the AWS Marketplace account on record, the
  // wizard moves on by itself (the backend binds the deploy to that same account, SEC-004). Any other account, or none
  // on record, still asks the customer to confirm it (SEC-002) and waits for IPC Global's approval.
  const hasMarketplaceAccountId = typeof customer.awsAccountId === 'string' && ACCOUNT_ID_RE.test(customer.awsAccountId);
  const accountMismatch = Boolean(detected) && hasMarketplaceAccountId && customer.awsAccountId !== detected.accountId;
  const accountMatches = Boolean(detected) && !awsAccountNeedsConfirmation(customer, detected.accountId);
  const advancedRef = useRef(false);
  useEffect(() => {
    if (!accountMatches || advancedRef.current) return;
    advancedRef.current = true;
    onNext({ ...customer, trustRoleArn: detected.trustRoleArn, trustRoleAccountId: detected.accountId });
  }, [accountMatches]); // eslint-disable-line react-hooks/exhaustive-deps
  // SEC-004: onGuard deploys only into the Marketplace account or one IPC
  // Global approved, so Continue waits for that approval.
  // ENTRAID-272: the approval is requested from here (support and the
  // operator topic are told), then this page polls the record so it moves
  // on by itself once IPC Global approves.
  const [approval, setApproval] = useState(() => ({
    trustAccountApproved: customer.trustAccountApproved,
    trustAccountApprovedAt: customer.trustAccountApprovedAt,
    request: customer.trustAccountApprovalRequest ?? null,
  }));
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [approvalError, setApprovalError] = useState(null);
  const accountApproved = Boolean(detected) && approval.trustAccountApproved === detected.accountId && Boolean(approval.trustAccountApprovedAt);
  const accountUnknown = Boolean(detected) && !hasMarketplaceAccountId;
  const needsApproval = (accountMismatch || accountUnknown) && !accountApproved;
  const approvalRequested = needsApproval && approval.request?.accountId === detected?.accountId;
  const approvalMailto = detected
    ? `mailto:support@ipc-global.com?subject=${encodeURIComponent(`onGuard: approve AWS account ${detected.accountId} for ${customer.customerId}`)}`
    : null;

  async function requestApproval() {
    setApprovalBusy(true); setApprovalError(null);
    try {
      const r = await api.requestTrustAccountApproval(customer.customerId);
      if (r?.state === 'approved') {
        setApproval((a) => ({ ...a, trustAccountApproved: r.accountId, trustAccountApprovedAt: a.trustAccountApprovedAt ?? new Date().toISOString() }));
      } else {
        setApproval((a) => ({ ...a, request: { accountId: r?.accountId ?? detected.accountId, requestedAt: r?.requestedAt ?? new Date().toISOString() } }));
        setAnnouncement('Approval requested. IPC Global support has been notified.');
      }
    } catch (e) {
      setApprovalError(e.message);
    } finally {
      setApprovalBusy(false);
    }
  }

  useEffect(() => {
    if (!approvalRequested) return undefined;
    let stopped = false;
    const poll = async () => {
      try {
        const r = await api.getCustomer(customer.customerId);
        const c = r?.customer ?? r;
        if (!stopped && c) {
          setApproval((a) => ({ ...a, trustAccountApproved: c.trustAccountApproved, trustAccountApprovedAt: c.trustAccountApprovedAt }));
        }
      } catch { /* keep waiting */ }
    };
    const timer = setInterval(poll, TRUST_APPROVAL_POLL_MS);
    return () => { stopped = true; clearInterval(timer); };
  }, [approvalRequested, customer.customerId]);

  const checkTrust = useCallback(async () => {
    if (doneRef.current) return;
    try {
      const r = await api.getTrustStatus(customer.customerId, probeAccount || undefined);
      if (r?.state === 'ready' && r.trustRoleArn) {
        doneRef.current = true;
        setDetected({ trustRoleArn: r.trustRoleArn, accountId: r.accountId });
        setAnnouncement(awsAccountNeedsConfirmation(customer, r.accountId)
          ? `AWS stack detected in account ${formatAccountId(r.accountId)}. Confirm the account to continue.`
          : `AWS stack detected in account ${formatAccountId(r.accountId)}.`);
      }
    } catch {
      // Keep waiting: a transient error must not end detection.
    }
  }, [customer, probeAccount]);

  useEffect(() => {
    let timer;
    let stopped = false;
    let attempt = 0;
    const loop = async () => {
      await checkTrust();
      if (stopped || doneRef.current) return;
      attempt += 1;
      if (attempt > TRUST_POLL_MAX_ATTEMPTS) return;
      timer = setTimeout(loop, TRUST_POLL_DELAYS_MS[Math.min(attempt - 1, TRUST_POLL_DELAYS_MS.length - 1)]);
    };
    loop();
    const onFocus = () => { if (document.visibilityState !== 'hidden') checkTrust(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      stopped = true;
      clearTimeout(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [checkTrust, pollEpoch]);

  function submitAccountId(e) {
    e.preventDefault();
    const id = accountId.replace(/[\s-]/g, '');
    if (!ACCOUNT_ID_RE.test(id)) { setAccountError('Enter the 12-digit AWS account ID.'); return; }
    setAccountError(null);
    setProbeAccount(id); // restarts detection with the assume-role fallback
  }

  return (
    <SectionCard as="div" bodyClassName="space-y-5">
      <div>
        <h2>Step 2: Set up AWS access</h2>
        <p className="text-sm text-ipc-muted mt-1">
          onGuard needs a secure connection to your AWS account to deploy and manage
          the data collector. This is done by creating an IAM role using an AWS CloudFormation stack.
        </p>
      </div>

      {/* ENTRAID-272: which AWS account onGuard deploys into, in plain sight. */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm space-y-1" data-testid="deployment-account-notice">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Deployment account</p>
        {marketplaceAccountId ? (
          <>
            <p className="text-slate-800">
              <span className="font-mono text-base">{formatAccountId(marketplaceAccountId)}</span>{' '}
              <span className="text-slate-600">(the AWS account of your AWS Marketplace subscription)</span>
            </p>
            <p className="text-slate-600">
              onGuard deploys into this account. Sign in to it before you launch the stack.
              To use a different AWS account, launch the stack there instead: IPC Global approves
              that account before onGuard deploys, and this page tells you when it is approved.
            </p>
          </>
        ) : (
          <p className="text-slate-600">
            No AWS Marketplace account is on record for you. Launch the stack in the AWS account
            you want onGuard in: IPC Global approves that account before onGuard deploys, and this
            page tells you when it is approved.
          </p>
        )}
      </div>

      {/* What you need box */}
      <div className="rounded-xl border border-ipc-border p-4 text-sm space-y-2" style={{ borderLeft: '4px solid var(--blue)' }}>
        <p className="font-semibold text-ipc-ink">What you'll need for this step:</p>
        <ul className="list-disc ml-5 text-ipc-ink space-y-1">
          <li>Access to the <strong>AWS Management Console</strong> for the account where you want onGuard deployed</li>
          <li>Permission to <strong>create CloudFormation stacks</strong> and <strong>IAM roles</strong> (typically an Administrator or PowerUser role)</li>
        </ul>
      </div>

      {/* Step-by-step instructions (same steps as the AWS administrator email) */}
      <div className="space-y-3">
        <p className="text-sm font-semibold text-slate-700">Follow these steps:</p>
        <ol className="list-decimal ml-5 text-sm text-slate-600 space-y-2">
          <li>
            <strong>Sign in to the AWS Management Console</strong> with the AWS account where onGuard
            will be deployed{marketplaceAccountId && <> (<span className="font-mono">{formatAccountId(marketplaceAccountId)}</span>)</>}.{' '}
            <a className="text-ipc-light hover:underline" href={AWS_CONSOLE_SIGN_IN_URL} target="_blank" rel="noopener noreferrer">
              Open the AWS sign-in page
            </a>
          </li>
          <li>
            In the same browser, click <strong>"Launch stack in AWS Console"</strong> below. It opens
            AWS CloudFormation with every parameter already filled in.
          </li>
          <li>
            At the bottom of the page, check <strong>"{IAM_ACKNOWLEDGMENT}"</strong>
          </li>
          <li>
            Click <strong>"Create stack"</strong>. The stack reaches{' '}
            <code className="bg-slate-100 px-1 rounded text-xs">CREATE_COMPLETE</code>{' '}
            in about a minute, and this page detects it on its own.
          </li>
        </ol>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        {launchLoading ? (
          <button type="button" className="btn-primary flex-1 text-center opacity-60 cursor-not-allowed" disabled>
            Preparing link...
          </button>
        ) : (
          <a className="btn-primary flex-1 text-center" href={launchStackUrl} target="_blank" rel="noopener noreferrer">
            Launch stack in AWS Console
          </a>
        )}
        <button type="button" className="btn-secondary" onClick={toggleTemplate}>
          {showTemplate ? 'Hide template' : 'Review template first'}
        </button>
      </div>

      {launchFallback && !launchLoading && (
        <p className="flex items-start gap-2 text-xs text-ipc-ink">
          <Dot tone="warn" className="mt-1" />
          Automatic detection is off for this link. After the stack is created, enter the AWS
          account ID below.
        </p>
      )}

      {showTemplate && (
        <TemplatePreview body={templateBody} error={templateError} />
      )}

      <details className="text-xs text-slate-500">
        <summary className="cursor-pointer hover:text-slate-700 font-medium">What does this CloudFormation stack create?</summary>
        <p className="mt-2 text-slate-600">
          It creates a single <strong>IAM role</strong> (<code>IPCOnGuardTrustRole</code>) that allows
          IPC Global's portal to deploy resources in your account with a scoped-down policy. The role can only
          manage resources tagged for onGuard. It cannot access any of your other AWS resources.
          It also tells onGuard when the role is created or deleted, so onboarding continues on its own;
          no code runs in your account for this. You can review the full template by clicking
          "Review template first" above.
        </p>
      </details>

      {/* ENTRAID-350: once the stack exists, the AWS administrator has nothing left to do: one done line, no form. */}
      {detected ? (
        <p className="flex items-center gap-2 text-sm text-ipc-ink" data-testid="aws-admin-done">
          <Dot tone="good" />
          The AWS stack is created. No instructions need to be sent.
        </p>
      ) : (
        launchStackUrl && <AwsAdminInstructions customer={customer} launchStackUrl={launchStackUrl} />
      )}

      {/* Detection status */}
      <div className="border-t border-slate-200 pt-4 space-y-3">
        {detected ? (
          <div className="space-y-3" data-trust-detection="ready">
            <p className="text-sm text-emerald-700" data-testid="trust-detected">
              Detected in account <span className="font-mono">{formatAccountId(detected.accountId)}</span>.
            </p>
            {accountMismatch && !accountApproved && (
              <div className="rounded-md border border-ipc-border p-3 text-sm text-ipc-ink" style={{ borderLeft: '4px solid var(--warn)' }} role="alert">
                This is not the AWS account of your AWS Marketplace subscription
                ({formatAccountId(customer.awsAccountId)}). Check that the stack was created in
                the right account. IPC Global approves a different account before onGuard
                deploys there.
              </div>
            )}
            {accountUnknown && !accountApproved && (
              <div className="rounded-md border border-ipc-border p-3 text-sm text-ipc-ink" style={{ borderLeft: '4px solid var(--warn)' }} role="alert">
                IPC Global approves this AWS account before onGuard deploys there.
              </div>
            )}
            {accountMatches && (
              <p className="text-xs text-emerald-600">Matches your AWS Marketplace account. Starting the install.</p>
            )}
            {!accountMatches && accountApproved && (
              <p className="text-xs text-emerald-600">Approved by IPC Global.</p>
            )}
            {needsApproval ? (
              approvalRequested ? (
                <div className="rounded-md border border-ipc-border p-3 text-sm text-ipc-ink" style={{ borderLeft: '4px solid var(--blue)' }} data-testid="approval-requested">
                  Approval requested for account <span className="font-mono">{formatAccountId(detected.accountId)}</span>.
                  IPC Global support has been notified. This page checks every 30 seconds and lets you
                  continue as soon as the account is approved. Questions:{' '}
                  <a className="underline" href={approvalMailto}>support@ipc-global.com</a>.
                </div>
              ) : (
                <div className="space-y-2">
                  <button type="button" className="btn-primary" onClick={requestApproval} disabled={approvalBusy}>
                    {approvalBusy ? 'Requesting...' : 'Request approval from IPC Global'}
                  </button>
                  {approvalError && (
                    <p className="text-sm text-red-700" role="alert">
                      {approvalError} You can also write to{' '}
                      <a className="underline" href={approvalMailto}>support@ipc-global.com</a>.
                    </p>
                  )}
                </div>
              )
            ) : !accountMatches && (
              <button
                type="button"
                className="btn-primary"
                onClick={() => onNext({ ...customer, trustRoleArn: detected.trustRoleArn, trustRoleAccountId: detected.accountId })}
              >
                Continue with this account
              </button>
            )}
            {!accountMatches && (
              <button type="button" className="btn-secondary ml-2" onClick={notMyAccount}>
                Not my account, start over
              </button>
            )}
            {resetError && <p className="text-sm text-red-700" role="alert">{resetError}</p>}
          </div>
        ) : (
          <div className="flex items-start gap-2 text-sm" data-trust-detection="waiting">
            <svg className="mt-0.5 h-4 w-4 flex-shrink-0 animate-spin text-ipc-light" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-slate-600">
              Waiting for the stack. When it reaches <code className="bg-slate-100 px-1 rounded text-xs">CREATE_COMPLETE</code>,
              this page continues on its own.
            </p>
          </div>
        )}
        <p className="sr-only" role="status" aria-live="polite">{announcement}</p>

        <details className="text-sm text-slate-600">
          <summary className="cursor-pointer text-xs font-medium text-slate-500 hover:text-slate-700">
            Stack created but not detected after a few minutes?
          </summary>
          <div className="mt-3 space-y-3">
            <form onSubmit={submitAccountId} className="space-y-2">
              <label htmlFor="trust-account-id" className="label">AWS account ID where you created the stack</label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  id="trust-account-id"
                  className="input font-mono sm:max-w-xs"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="123456789012"
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                />
                <button className="btn-secondary">Check this account</button>
              </div>
              {accountError && <p className="text-xs text-red-700" role="alert">{accountError}</p>}
              {probeAccount && !accountError && !detected && (
                <p className="text-xs text-slate-500">Checking account {formatAccountId(probeAccount)} until the role can be reached.</p>
              )}
            </form>
          </div>
        </details>
      </div>
    </SectionCard>
  );
}

// "Don't have AWS access?" (ENTRAID-260): email the steps and the launch link
// to the customer's AWS administrator, or copy them.
function AwsAdminInstructions({ customer, launchStackUrl }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { kind: 'ok'|'err', text }
  const [copied, setCopied] = useState(null); // null | 'ok' | 'failed'
  const instructions = trustStackInstructionsText({ companyName: customer.companyName, launchUrl: launchStackUrl });

  async function send(e) {
    e.preventDefault();
    setBusy(true); setResult(null);
    try {
      const r = await api.sendAwsAdminInstructions(customer.customerId, email.trim());
      setResult({ kind: 'ok', text: `Instructions sent to ${r?.to ?? email.trim()}.` });
      setEmail('');
    } catch (err) {
      setResult({ kind: 'err', text: err.message || 'Sending failed. Use Copy instructions instead.' });
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(instructions);
      setCopied('ok');
    } catch {
      setCopied('failed');
    }
  }

  return (
    <section aria-labelledby="aws-admin-heading" className="rounded-lg border border-slate-200 p-4 space-y-3">
      <div>
        <h3 id="aws-admin-heading" className="text-sm font-semibold text-slate-800">
          Don't have AWS access? Send instructions to your AWS administrator
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          We email them these steps and the launch link. This page still detects the stack when they create it.
        </p>
      </div>
      <form onSubmit={send} className="flex flex-col gap-2 sm:flex-row">
        <label htmlFor="aws-admin-email" className="sr-only">AWS administrator email</label>
        <input
          id="aws-admin-email"
          type="email"
          className="input sm:flex-1"
          autoComplete="off"
          placeholder="aws-admin@company.com"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <button className="btn-primary disabled:opacity-60" disabled={busy || !email.trim()}>
          {busy ? 'Sending…' : 'Send'}
        </button>
      </form>
      <div aria-live="polite">
        {result && (
          <p role={result.kind === 'err' ? 'alert' : 'status'}
            className={`text-xs ${result.kind === 'ok' ? 'text-emerald-700' : 'text-red-700'}`}>
            {result.text}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="btn-secondary text-sm" onClick={copy}>
          {copied === 'ok' ? 'Instructions copied' : 'Copy instructions'}
        </button>
        {copied === 'ok' && <span className="sr-only" role="status">Instructions copied to the clipboard.</span>}
      </div>
      {copied === 'failed' && (
        <div className="space-y-1">
          <label htmlFor="aws-admin-instructions-manual" className="label">
            Your browser blocked copying. Select the text below and copy it manually.
          </label>
          <textarea
            id="aws-admin-instructions-manual"
            className="input font-mono text-xs h-40"
            readOnly
            value={instructions}
            onFocus={(e) => e.target.select()}
          />
        </div>
      )}
    </section>
  );
}

function TemplatePreview({ body, error }) {
  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        Could not load template: {error}
      </div>
    );
  }
  if (!body) return <PageState state="loading" cards={0} label="Loading the template" />;
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50">
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 text-xs text-slate-500">
        <span>customer-trust-stack.yaml: {body.split('\n').length} lines</span>
        <button type="button" className="text-ipc-light hover:underline"
          onClick={() => navigator.clipboard.writeText(body)}>
          Copy
        </button>
      </div>
      <pre className="overflow-auto max-h-[420px] p-3 text-xs leading-relaxed font-mono whitespace-pre">
{body}
      </pre>
    </div>
  );
}

/* ---------- Helpers ---------- */

function ErrorCard({ message }) {
  return <PageState state="error" title="Something went wrong" message={message} />;
}

function Loading() {
  return <PageState state="loading" cards={0} label="Loading your onboarding" />;
}
